import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { DateFormatter } from '@repo/toolkit/date'
import { NumberFormatter } from '@repo/toolkit/number'
import { StringFormatter } from '@repo/toolkit/string'
import { useDebounce } from '@repo/toolkit/use-debounce'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import {
  BellIcon,
  CreditCardIcon,
  EmailIcon,
  MessageIcon,
  PlusIcon,
  TelephoneIcon
} from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery } from '@tanstack/react-query'
import { isBefore } from 'date-fns'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { addCommentToEnrollmentAccounting } from '../../api/enrollment-accountings/add-comment-to-enrollment-accounting'
import { getEnrollmentAccountingPaymentsInfo } from '../../api/enrollment-accountings/get-enrollment-accounting-payments-info'
import { sendEnrollmentAccountingEmailReminder } from '../../api/enrollment-accountings/send-enrollment-accounting-email-reminder'
import { sendEnrollmentAccountingPhoneReminder } from '../../api/enrollment-accountings/send-enrollment-accounting-phone-reminder'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SingleComment from '../../components/elements/single-comment'
import UpdateCommentForm from '../../components/elements/update-comment-form'
import RemindersHistory from '../../components/pages/enrollments/reminders-history'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { useUrl } from '../../lib/hooks/use-url'
import { PaymentStatusEnum } from '../../lib/types/enums/enrollment/payment-status.enum'
import { PaymentTypeEnum } from '../../lib/types/enums/payment-record/payment-type.enum'
import { StatusEnum } from '../../lib/types/enums/payment-record/status.enum'
import { EnrollmentAccountingFilters } from '../../lib/types/models/enrollment/enrollment-accounting-filters'
import { EnrollmentAccounting } from '../../lib/types/models/enrollment/enrollment-light'
import { PaymentRecord } from '../../lib/types/models/payment-record/payment-record'
import {
  transformEnrollmentAccountingFiltersToUrl,
  transformUrlToEnrollmentAccountingFilters
} from '../../lib/utils/filters-mapping/enrollment-accounting'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import EnrollmentAccountingFilter from './enrollment-accounting-filter'

export const enrollmentAccountingInitialFilter: EnrollmentAccountingFilters = {
  countryCodes: []
}

const ClientPaymentsListPage = () => {
  const tableRef = useRef<DynamicTableRef<EnrollmentAccounting>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)
  const { readPagination } = useUrl()
  const [search, setSearch] = useState(() => readPagination().q)
  // Same 300ms delay as the table, so the totals follow the search the list applies
  const { debouncedCallback: debouncedSetSearch } = useDebounce(setSearch, 300)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<EnrollmentAccountingFilters>({
      initialFilters: enrollmentAccountingInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformEnrollmentAccountingFiltersToUrl,
      transformUrlToFilter: transformUrlToEnrollmentAccountingFilters
    })

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'filters' | 'confirm-email-reminder',
    EnrollmentAccounting[]
  >(['filters', 'confirm-email-reminder'])

  const {
    modalState: commentModal,
    selectedData: comment,
    toggleModal: toggleComment,
    closeModel: closeComment
  } = useModalManager<'add-comment', EnrollmentAccounting>(['add-comment'])

  const {
    modalState: reminderModalState,
    selectedData: reminderSelectedData,
    toggleModal: reminderToggleModal,
    closeModel: reminderCloseModel
  } = useModalManager<'reminder', number>(['reminder'])

  const { mutateAsync: addCommentToEnrollmentAccountingMutation } = useMutation({
    mutationFn: addCommentToEnrollmentAccounting,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Commentaire ajouté avec succès')
      tableRef.current?.refetch()
    }
  })

  const sendEnrollmentAccountingEmailReminderMutation = useMutation({
    mutationFn: sendEnrollmentAccountingEmailReminder,
    onError: showAxiosError,
    onSuccess: ({ sentCount, skippedCount }) => {
      if (sentCount > 0) toast.success(`${sentCount} relance(s) par email envoyée(s)`)
      // clients with no pending instalment, a cancelled stay or no contact email are not reminded
      if (skippedCount > 0)
        toast.warning(
          `${skippedCount} client(s) non relancé(s) : aucune échéance à régler, séjour annulé ou aucun email de contact`
        )
      tableRef.current?.refetch()
    }
  })

  const { data: enrollmentAccountingPaymentsInfo } = useQuery({
    initialData: {
      totalAmountReceived: 0,
      totalAmountToCollect: 0,
      totalOverpaymentToRefund: 0,
      totalPropositionPricesAfterDiscount: 0
    },
    queryKey: ['enrollment-accounting-payments-info', selectedFilter, search],
    queryFn: () =>
      getEnrollmentAccountingPaymentsInfo({ filter: selectedFilter, search: search?.trim() })
  })

  const getNumberOfInstallments = (paymentRecords: PaymentRecord[]) => {
    return paymentRecords.filter(
      (record) =>
        record.type === PaymentTypeEnum.PAYMENT &&
        (record.status === StatusEnum.SUCCEEDED || record.status === StatusEnum.PENDING)
    ).length
  }

  const getSoldePaymentRecord = (enrollment: EnrollmentAccounting) => {
    const soldeAmount = enrollment.soldeAmount
    const soldeDate = enrollment.soldeDate
    if (!soldeAmount || !soldeDate) return null
    const isDue = isBefore(new Date(soldeDate), new Date())
    const numberOfInstallments = getNumberOfInstallments(enrollment.paymentRecords)
    const amount =
      numberOfInstallments <= 1
        ? (enrollment.mainProposition.totalPriceAfterDiscount ?? 0)
        : soldeAmount
    const isSoldePaid = enrollment.paymentStatus === PaymentStatusEnum.FULLY_PAID
    return { isSoldePaid, amount, soldeDate, isDue }
  }

  const columns: Column<EnrollmentAccounting>[] = [
    {
      accessorKey: 'paymentStatus',
      header: 'Paiement',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          icon={
            <CreditCardIcon
              color={
                row.original.paymentStatus === PaymentStatusEnum.FULLY_PAID
                  ? tailwindColors.green.DEFAULT
                  : tailwindColors.red.DEFAULT
              }
            />
          }
          variant={
            row.original.paymentStatus === PaymentStatusEnum.FULLY_PAID ? 'success' : 'danger'
          }
        />
      )
    },
    {
      accessorKey: 'childLastName',
      header: 'NOM Prénom',
      enableSorting: true,
      cell: ({ row }) => (
        <Typography.Link
          onClick={() => {
            window.open(Paths.ENROLLMENT_PROFILE_PAGE.replace(':id', row.original.id.toString()))
          }}
          variant="label-medium"
        >
          {row.original.childLastName?.toUpperCase()}{' '}
          {row.original.childFirstName
            ? StringFormatter.capitalize(row.original.childFirstName)
            : ''}
        </Typography.Link>
      )
    },
    {
      accessorKey: 'confirmedAt',
      header: 'Inscription confirmée',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.confirmedAt ? (
          <Badge text={DateFormatter.DDMMYYYY(row.original.confirmedAt)} />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'mainProposition.journey.date.product.country.name',
      header: 'Pays',
      cell: ({ row }) => (
        <CountryFlag code={row.original.mainProposition.journey?.date.product.country.code} />
      )
    },
    {
      accessorKey: 'enrollmentCenter.center.name',
      header: 'Centre',
      enableSorting: true,
      cell: ({ row }) => valueRender(row.original.enrollmentCenter?.center.name)
    },
    {
      accessorKey: 'mainProposition.totalPriceAfterDiscount',
      header: 'TOTAL SEJOUR',
      enableSorting: true,
      cell: ({ row }) => {
        const total = row.original.mainProposition.totalPriceAfterDiscount
        if (!total) return '-'
        return (
          <Badge text={`${NumberFormatter.roundToTwoDecimalPlaces(total)}€`} variant={'primary'} />
        )
      }
    },
    {
      accessorKey: 'viewData.amountReceived',
      header: 'Montant encaissé',
      enableSorting: true,
      cell: ({ row }) => {
        const amountReceived = row.original.amountReceived
        if (!amountReceived) return '-'
        return (
          <Badge
            text={`${NumberFormatter.roundToTwoDecimalPlaces(amountReceived)}€`}
            variant={'primary'}
          />
        )
      }
    },
    {
      accessorKey: 'viewData.amountRemaining',
      header: 'Montant restant',
      enableSorting: true,
      cell: ({ row }) => {
        const amountRemaining = row.original.amountRemaining
        return (
          <Badge
            text={`${NumberFormatter.roundToTwoDecimalPlaces(amountRemaining ?? 0)}€`}
            variant={'primary'}
          />
        )
      }
    },
    {
      accessorKey: 'viewData.depositAmount',
      header: 'Échéance 1',
      centerCell: true,
      cell: ({ row }) => {
        const amount = row.original.firstPaymentAmount
        const date = row.original.firstPaymentDate
        if (!amount || !date) return '-'
        const isDue = isBefore(new Date(date), new Date())
        const isDepositPaid = row.original.firstPaymentStatus === StatusEnum.SUCCEEDED
        return (
          <div className="flex flex-col gap-1 py-1 items-center w-32">
            {!isDepositPaid && (
              <Badge
                text={`${NumberFormatter.roundToTwoDecimalPlaces(amount)}€`}
                variant={'primary'}
              />
            )}
            <Badge
              text={DateFormatter.DDMMYYYY(date)}
              variant={isDepositPaid ? 'success' : isDue ? 'danger' : 'grey'}
            />
          </div>
        )
      }
    },
    {
      accessorKey: 'viewData.soldeAmount',
      header: 'Solde',
      centerCell: true,
      cell: ({ row }) => {
        const result = getSoldePaymentRecord(row.original)
        if (!result) return '-'
        const { isSoldePaid, amount, soldeDate, isDue } = result
        return (
          <div className="flex flex-col gap-1 py-1 items-center">
            {!isSoldePaid && (
              <Badge
                text={`${NumberFormatter.roundToTwoDecimalPlaces(amount)}€`}
                variant={'primary'}
              />
            )}
            <Badge
              text={DateFormatter.DDMMYYYY(soldeDate)}
              variant={isSoldePaid ? 'success' : isDue ? 'danger' : 'grey'}
            />
          </div>
        )
      }
    },
    {
      accessorKey: 'optinForAutomaticDebit',
      header: 'Prélèvement automatique',
      enableSorting: true,
      cell: ({ row }) => (
        <Badge
          text={row.original.optinForAutomaticDebit ? 'Oui' : 'Non'}
          variant={row.original.optinForAutomaticDebit ? 'success' : 'danger'}
        />
      )
    },
    {
      accessorKey: 'accountingReminderByEmailSentAt',
      header: 'Relance mail',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.accountingReminderByEmailSentAt ? (
          <Badge.Link
            onClick={() => reminderToggleModal('reminder', row.original.id)}
            text={`R${row.original.accountingReminderByEmailsSent} - ${DateFormatter.DDMMYYYY(row.original.accountingReminderByEmailSentAt)}`}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'accountingReminderByPhoneSentAt',
      header: 'Relance tel',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.accountingReminderByPhoneSentAt ? (
          <Badge.Link
            onClick={() => reminderToggleModal('reminder', row.original.id)}
            text={`R${row.original.accountingReminderByPhoneSent} - ${DateFormatter.DDMMYYYY(row.original.accountingReminderByPhoneSentAt)}`}
          />
        ) : (
          '-'
        )
    },
    {
      accessorKey: 'accountingComments',
      header: 'Commentaire',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.accountingComments ? (
          <SingleComment
            comment={row.original.accountingComments}
            onClick={() => toggleComment('add-comment', row.original)}
          />
        ) : (
          <Button
            icon={PlusIcon}
            label="Ajouter commentaire"
            onClick={() => toggleComment('add-comment', row.original)}
          />
        )
    }
  ]

  return (
    <div>
      <UserNavBar title="Paiement client" icon={CreditCardIcon} />
      <div className="h-4" />
      <DynamicDataTable<EnrollmentAccounting, EnrollmentAccountingFilters>
        title="Tous les paiements client"
        onLoadingChange={setIsTableLoading}
        columns={columns}
        ref={tableRef}
        selectable
        endpoint={API['enrollment-accountings'].getAll.url}
        allowSearchParam
        onSearchChange={debouncedSetSearch}
        permissions={{ delete: false }}
        onFiltersClick={() => toggleModal('filters')}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        footerCells={[
          {
            accessorKey: 'paymentStatus',
            value: (
              <Typography variant="tag" color={tailwindColors.orange.DEFAULT}>
                Total
              </Typography>
            )
          },
          {
            accessorKey: 'mainProposition.totalPriceAfterDiscount',
            value: (
              <Badge
                variant="orange"
                text={NumberFormatter.formatCurrency(
                  enrollmentAccountingPaymentsInfo.totalPropositionPricesAfterDiscount
                )}
              />
            )
          },
          {
            accessorKey: 'viewData.amountReceived',
            value: (
              <Badge
                variant="orange"
                text={NumberFormatter.formatCurrency(
                  enrollmentAccountingPaymentsInfo.totalAmountReceived
                )}
              />
            )
          },
          {
            accessorKey: 'viewData.amountRemaining',
            value: (
              <div className="flex flex-col gap-2 py-2">
                <Badge
                  variant="orange"
                  textClassName="whitespace-nowrap"
                  text={`Reste à encaisser : ${NumberFormatter.formatCurrency(
                    enrollmentAccountingPaymentsInfo.totalAmountToCollect
                  )}`}
                />
                <Badge
                  variant="orange"
                  textClassName="whitespace-nowrap"
                  text={`Trop-perçu à rembourser : ${NumberFormatter.formatCurrency(
                    enrollmentAccountingPaymentsInfo.totalOverpaymentToRefund
                  )}`}
                />
              </div>
            )
          }
        ]}
        actions={[
          {
            type: 'multiple',
            icon: EmailIcon,
            variant: 'ghost',
            label: 'Relancer par email',
            fn: (enrollments) => toggleModal('confirm-email-reminder', enrollments)
          },
          {
            type: 'multiple',
            icon: TelephoneIcon,
            variant: 'ghost',
            refetch: true,
            label: 'Relancer par tél',
            fn: async (enrollments) => {
              await sendEnrollmentAccountingPhoneReminder(enrollments.map((e) => e.id))
            }
          }
        ]}
      />

      {selectedData && (
        <Modal
          isOpen={modalState['confirm-email-reminder']}
          icon={BellIcon}
          size="small"
          isLoading={sendEnrollmentAccountingEmailReminderMutation.isPending}
          actionButtonsOptions={{
            cancelText: 'Annuler',
            confirmText: 'Envoyer',
            onConfirm: async () => {
              await sendEnrollmentAccountingEmailReminderMutation.mutateAsync(
                selectedData.map((e) => e.id)
              )
              closeModel('confirm-email-reminder')
            }
          }}
          variant="confirmation"
          title="Êtes-vous sûr de vouloir relancer le client par email concernant les informations à compléter de son Espace Personnel ?"
          onCancel={() => closeModel('confirm-email-reminder')}
        />
      )}

      {reminderSelectedData && (
        <Modal
          isOpen={reminderModalState.reminder}
          title={'Historique des relances'}
          icon={BellIcon}
          hideActions
          size="medium"
          onCancel={() => reminderCloseModel('reminder')}
        >
          <RemindersHistory
            className={MODAL_BODY_CLASSNAME}
            enrollmentId={reminderSelectedData}
            type="accounting"
          />
        </Modal>
      )}

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
        isLoading={isTableLoading}
      >
        <EnrollmentAccountingFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>

      <Modal
        isOpen={commentModal['add-comment']}
        title={comment ? 'Modifier commentaire' : 'Ajouter commentaire'}
        size="small"
        hideActions
        icon={MessageIcon}
        onCancel={() => closeComment('add-comment')}
      >
        {comment && (
          <UpdateCommentForm
            defaultComment={comment.accountingComments}
            className={MODAL_BODY_CLASSNAME}
            onCancel={() => closeComment('add-comment')}
            onSuccess={async (commentText) => {
              await addCommentToEnrollmentAccountingMutation({
                enrollmentId: comment.id,
                comment: commentText
              })
              closeComment('add-comment')
            }}
          />
        )}
      </Modal>
    </div>
  )
}

export default ClientPaymentsListPage
